import { t } from "@/i18n-js/instance";
import { CheckoutUrlWithCouponSelector } from "@/react/components/Paywalls/Admin/PaywallsAdmin/FormModal/DetailsTab/CheckoutUrlWithCouponSelector";
import { SupportedCurrencyChooser } from "@circle-react/components/SupportedCurrencyChooser";
import { retrievePaywallCheckoutHostname } from "@circle-react/helpers/paywallCheckoutHelpers";
import { Form } from "@circle-react-uikit/Form";
import { TabContainer } from "../../components/TabContainer";
import { TabHeader } from "../../components/TabHeader";
import { useDetailsTab } from "./hooks/useDetailsTab";

interface DetailsTabProps {
  canChangeCurrency: boolean;
  requiresCurrency: boolean;
}

export const DetailsTab = ({
  requiresCurrency,
  canChangeCurrency,
}: DetailsTabProps) => {
  const { copyWithPrefix } = useDetailsTab();
  const renderCopyCheckoutComponent = () => (
    <button
      type="button"
      onClick={copyWithPrefix}
      className="text-lightest hover:text-default"
    >
      {t("copy_link")}
    </button>
  );

  return (
    <TabContainer>
      <TabHeader title={t("settings.paywalls.modal.tabs.details.title")} />
      <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
        <Form.Item
          className="!mt-0 !py-0"
          name="display_name"
          translationRoot="settings.paywalls.modal.tabs.details"
          hideBorder
          tooltipText={t(
            "settings.paywalls.modal.tabs.details.display_name_tooltip",
          )}
          hideDescription
          fullWidth={false}
          rules={{
            required: t(
              "settings.paywalls.modal.tabs.details.alerts.display_name_is_required",
            ),
            maxLength: 255,
          }}
          hidePlaceholder
        >
          <Form.Input />
        </Form.Item>
        <Form.Item
          className="!mt-0 !py-0"
          name="name"
          translationRoot="settings.paywalls.modal.tabs.details"
          hideBorder
          fullWidth={false}
          tooltipText={t("settings.paywalls.modal.tabs.details.name_tooltip")}
          hideDescription
          hidePlaceholder
          rules={{
            maxLength: 255,
          }}
        >
          <Form.Input />
        </Form.Item>
      </div>

      <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
        <Form.Item
          className="!mt-0 !py-0"
          name="subtitle"
          translationRoot="settings.paywalls.modal.tabs.details"
          hideBorder
          fullWidth={false}
          tooltipText={t(
            "settings.paywalls.modal.tabs.details.subtitle_tooltip",
          )}
          hideDescription
          hidePlaceholder
          rules={{
            maxLength: 70,
          }}
        >
          <Form.Input maxLength="70" />
        </Form.Item>

        {requiresCurrency && (
          <SupportedCurrencyChooser
            formItemClassName="!mt-0 !py-0"
            formItemName="currency_id"
            formItemTranslationRoot="settings.paywalls.modal.tabs.details"
            formItemRules={{
              required: t(
                "settings.paywalls.modal.tabs.details.alerts.currency_is_required",
              ),
            }}
            formItemTooltipText={t(
              "settings.paywalls.modal.tabs.details.currency_id_tooltip",
            )}
            disabled={!canChangeCurrency}
          />
        )}
      </div>

      <div className="flex flex-col gap-y-4">
        <Form.Item
          className="!mt-0 !py-0"
          labelWrapperClassName="inline-flex justify-between"
          name="checkout_path"
          translationRoot="settings.paywalls.modal.tabs.details"
          hideBorder
          fullWidth={false}
          hidePlaceholder
          rules={{
            required: t(
              "settings.paywalls.modal.tabs.details.alerts.checkout_path_is_required",
            ),
            maxLength: 255,
          }}
          description={renderCopyCheckoutComponent()}
          variant="checkout-path"
        >
          <Form.Input
            prefix={<p className="p-3">{retrievePaywallCheckoutHostname()}</p>}
            prefixWrapperClassName="sm:whitespace-normal md:whitespace-nowrap"
          />
        </Form.Item>
        <CheckoutUrlWithCouponSelector />
      </div>
      <Form.Item
        className="!mt-0 !py-0"
        name="description"
        translationRoot="settings.paywalls.modal.tabs.details"
        hideBorder
        fullWidth={false}
        hideDescription
      >
        <Form.TextArea name="description" />
      </Form.Item>
    </TabContainer>
  );
};
